import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, authFileUrl } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DoorOpen, Plus, Trash2, Upload, FileText, ArrowDownToLine, ArrowUpFromLine, Truck, IndianRupee, Camera, Loader2, Sparkles, AlertTriangle, ImagePlus, Paperclip, X } from "lucide-react";

const FIELD_LABELS = { party: "Party", doc_type: "Document type", doc_no: "Doc No", item: "Item", lines: "Line items", quantity: "Quantity", unit: "Unit", vehicle_no: "Vehicle No", person: "Driver", driver_phone: "Driver phone", freight_amount: "Freight amount" };

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const TYPES = [
  { v: "purchase", l: "Purchase" },
  { v: "sales", l: "Sales" },
  { v: "service", l: "Service" },
  { v: "transfer", l: "Transfer" },
  { v: "job_work", l: "Job Work" },
];
const TYPE_L = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));
const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const emptyForm = {
  direction: "in", company_id: "", factory_id: "", factory_name: "", to_factory_id: "", to_factory_name: "",
  gate_type: "purchase", party: "", doc_type: "invoice", doc_no: "", attachment: "",
  lines: [{ item: "", quantity: "", unit: "nos" }], photos: [], documents: [],
  vehicle_no: "", person: "", driver_phone: "",
  freight_mode: "own_vehicle", freight_amount: "", freight_status: "pending", payment_mode: "",
  remarks: "", entry_date: "",
};

export default function Gate() {
  const { user } = useAuth();
  const canDelete = ["admin", "manager"].includes(user?.role);
  const canPayFreight = ["admin", "manager", "store_keeper"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [factories, setFactories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [tFactory, setTFactory] = useState("all");
  const [tType, setTType] = useState("all");
  const [tCompany, setTCompany] = useState("all");
  const [scanning, setScanning] = useState(false);
  const [uncertain, setUncertain] = useState([]);
  const uRing = (k) => (uncertain.includes(k) ? "ring-2 ring-ochre/70 border-ochre" : "");

  const load = () => api.get("/gate-entries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/factories").then((r) => setFactories(r.data || [])).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setF((p) => ({ ...p, lines: [...p.lines, { item: "", quantity: "", unit: "nos" }] }));
  const removeLine = (i) => setF((p) => ({ ...p, lines: p.lines.length > 1 ? p.lines.filter((_, idx) => idx !== i) : p.lines }));
  const removeAt = (key, i) => setF((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));
  const uploadFiles = async (files, key, asPath) => {
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        setF((p) => ({ ...p, [key]: asPath ? [...(p[key] || []), data.path] : [...(p[key] || []), { name: file.name, path: data.path, ext }] }));
      } catch { toast.error(`Could not upload ${file.name}`); }
    }
    toast.success("Uploaded");
  };
  const pickFactory = (id) => { const x = factories.find((c) => c.id === id); setF((p) => ({ ...p, factory_id: id, factory_name: x?.name || "" })); };
  const pickToFactory = (id) => { const x = factories.find((c) => c.id === id); setF((p) => ({ ...p, to_factory_id: id, to_factory_name: x?.name || "" })); };

  const pickCompany = (id) => { const x = companies.find((c) => c.id === id); setF((p) => ({ ...p, company_id: id, company_name: x?.name || "" })); };
  const openNew = () => { setF({ ...emptyForm, entry_date: nowLocal(), company_id: companies[0]?.id || "" }); setUncertain([]); setOpen(true); };

  const shrinkImage = (file) => new Promise((resolve) => {
    if (/pdf/i.test(file.type || file.name || "") || file.size < 900_000) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1800, s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      c.toBlob((b) => resolve(b ? new File([b], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleDoc = async (orig) => {
    setScanning(true);
    const file = await shrinkImage(orig);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd);
      set("attachment", up.data.path);
    } catch (e) { toast.error("Upload failed"); setScanning(false); return; }
    const isImg = (file.type || "").startsWith("image/") || (!file.type && !/\.pdf$/i.test(file.name || "")) || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    if (isImg) {
      try {
        const ef = new FormData(); ef.append("file", file);
        const { data } = await api.post("/gate-entries/extract", ef);
        setF((p) => ({
          ...p,
          party: data.party || p.party,
          doc_type: data.doc_type || p.doc_type,
          doc_no: data.doc_no || p.doc_no,
          lines: (Array.isArray(data.lines) && data.lines.length)
            ? data.lines.map((l) => ({ item: l.item || "", quantity: l.quantity ? String(l.quantity) : "", unit: l.unit || "nos" }))
            : p.lines,
          vehicle_no: data.vehicle_no || p.vehicle_no,
          person: data.person || p.person,
          driver_phone: data.driver_phone || p.driver_phone,
          freight_amount: data.freight_amount ? String(data.freight_amount) : p.freight_amount,
          freight_mode: data.freight_amount ? "to_pay" : p.freight_mode,
        }));
        setUncertain(Array.isArray(data.uncertain) ? data.uncertain : []);
        toast.success(`Auto-filled ${Array.isArray(data.lines) ? data.lines.length : 0} line item(s) — please review`);
      } catch (e) { toast.info(`Attached. Couldn't auto-read (${formatApiErrorDetail(e.response?.data?.detail) || e.message || "network"}) — please fill the fields manually.`); }
    } else {
      toast.success("Document attached");
    }
    setScanning(false);
  };

  const save = async () => {
    const lines = f.lines.filter((l) => l.item || Number(l.quantity)).map((l) => ({ item: l.item, quantity: Number(l.quantity) || 0, unit: l.unit || "nos" }));
    try {
      await api.post("/gate-entries", { ...f, lines, freight_amount: Number(f.freight_amount) || 0 });
      toast.success("Gate entry recorded");
      setOpen(false); setF(emptyForm); setUncertain([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this gate entry?")) return;
    await api.delete(`/gate-entries/${id}`); toast.success("Deleted"); load();
  };
  const payFreight = async (e) => {
    const mode = window.prompt("Freight paid via? (cash / phonepe / gpay / paytm / bank)", e.payment_mode || "phonepe");
    if (mode === null) return;
    await api.post(`/gate-entries/${e.id}/pay-freight`, { payment_mode: mode });
    toast.success("Freight marked paid"); load();
  };

  const cItems = useMemo(() => (tCompany === "all" ? items : items.filter((i) => i.company_id === tCompany)), [items, tCompany]);
  const shown = useMemo(() => cItems.filter((i) =>
    (tFactory === "all" || i.factory_id === tFactory || i.to_factory_id === tFactory) &&
    (tType === "all" || i.gate_type === tType)
  ), [cItems, tFactory, tType]);

  // Job-work reconciliation: material out for job work should equal material returned (in)
  const jw = useMemo(() => {
    const j = cItems.filter((i) => i.gate_type === "job_work");
    const out = j.filter((i) => i.direction === "out").reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    const inn = j.filter((i) => i.direction === "in").reduce((s, i) => s + (Number(i.quantity) || 0), 0);
    return { out, inn, pending: out - inn };
  }, [cItems]);

  const todayIn = cItems.filter((i) => i.direction === "in").length;
  const todayOut = cItems.filter((i) => i.direction === "out").length;

  const freight = useMemo(() => {
    const tp = cItems.filter((i) => i.freight_mode === "to_pay");
    const paid = tp.filter((i) => i.freight_status === "paid").reduce((s, i) => s + (Number(i.freight_amount) || 0), 0);
    const pending = tp.filter((i) => i.freight_status !== "paid").reduce((s, i) => s + (Number(i.freight_amount) || 0), 0);
    return { paid, pending, total: paid + pending };
  }, [cItems]);

  return (
    <div className="space-y-8">
      <PageHeader title="Gate / Security" subtitle="Guard log of goods in & out per factory, with invoice/challan proof." icon={DoorOpen}
        action={(
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && !f.entry_date) set("entry_date", nowLocal()); }}>
            <DialogTrigger asChild>
              <Button data-testid="gate-new-button" onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New Gate Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Record Gate Entry</DialogTitle></DialogHeader>
              <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
                {uncertain.length > 0 && (
                  <div data-testid="gate-uncertain-banner" className="flex items-start gap-2 rounded-md border border-ochre/40 bg-ochre/10 p-3 text-xs text-ochre">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>AI wasn't fully sure about: <b>{uncertain.map((k) => FIELD_LABELS[k] || k).join(", ")}</b>. Please double-check the highlighted field{uncertain.length > 1 ? "s" : ""} before saving.</span>
                  </div>
                )}
                <div className="space-y-1"><Label className="text-xs font-semibold">Company (guard picks per entry)</Label>
                  <Select value={f.company_id} onValueChange={pickCompany}>
                    <SelectTrigger data-testid="gate-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="space-y-1"><Label className="text-xs">Direction</Label>
                    <Select value={f.direction} onValueChange={(v) => set("direction", v)}>
                      <SelectTrigger data-testid="gate-direction"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="in">Inward (IN)</SelectItem><SelectItem value="out">Outward (OUT)</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Type</Label>
                    <Select value={f.gate_type} onValueChange={(v) => set("gate_type", v)}>
                      <SelectTrigger data-testid="gate-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">{f.gate_type === "transfer" ? "From factory" : "Factory"}</Label>
                    <Select value={f.factory_id} onValueChange={pickFactory}>
                      <SelectTrigger data-testid="gate-factory"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{factories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  {f.gate_type === "transfer" && (
                    <div className="space-y-1"><Label className="text-xs">To factory</Label>
                      <Select value={f.to_factory_id} onValueChange={pickToFactory}>
                        <SelectTrigger data-testid="gate-to-factory"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{factories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                  )}
                  <div className="space-y-1"><Label className="text-xs">Party (supplier / buyer / job-worker)</Label>
                    <Input data-testid="gate-party" value={f.party} onChange={(e) => set("party", e.target.value)} placeholder="M/s Kisan Timber" className={uRing("party")} /></div>
                  <div className="space-y-1"><Label className="text-xs">Date & time</Label>
                    <Input type="datetime-local" data-testid="gate-date" value={f.entry_date} onChange={(e) => set("entry_date", e.target.value)} /></div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="space-y-1"><Label className="text-xs">Document</Label>
                    <Select value={f.doc_type} onValueChange={(v) => set("doc_type", v)}>
                      <SelectTrigger data-testid="gate-doc-type" className={uRing("doc_type")}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="challan">Challan</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-1"><Label className="text-xs">Doc No</Label>
                    <Input data-testid="gate-doc-no" value={f.doc_no} onChange={(e) => set("doc_no", e.target.value)} className={uRing("doc_no")} /></div>
                  <div className="col-span-2 space-y-1"><Label className="flex items-center gap-1 text-xs"><Sparkles className="h-3 w-3 text-primary" /> Invoice / Challan photo — AI auto-fills the fields</Label>
                    {f.attachment ? (
                      <div className="flex items-center gap-2">
                        <a href={authFileUrl(f.attachment)} target="_blank" rel="noreferrer" data-testid="gate-attachment-link" className="flex items-center gap-1 truncate text-xs text-primary hover:underline"><FileText className="h-3.5 w-3.5" /> View document</a>
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => set("attachment", "")}>Remove / rescan</Button>
                      </div>
                    ) : (
                      <label className={`flex cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-2.5 text-xs font-semibold transition-colors ${scanning ? "border-primary bg-primary/5 text-primary" : "border-dashed border-primary/50 text-primary hover:bg-primary/5"}`}>
                        {scanning ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading document…</> : <><Camera className="h-4 w-4" /> Take / upload photo — auto-fill</>}
                        <input type="file" accept="image/*,application/pdf,.pdf" capture="environment" disabled={scanning} className="hidden" data-testid="gate-attachment-upload" onChange={(e) => e.target.files[0] && handleDoc(e.target.files[0])} />
                      </label>
                    )}
                  </div>
                </div>

                <div className={`space-y-2 rounded-md border p-3 ${uRing("lines")}`}>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Line items — all goods on the invoice</Label>
                    <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="gate-add-line" onClick={addLine}><Plus className="h-3 w-3" /> Add line</Button>
                  </div>
                  <div className="space-y-2">
                    {f.lines.map((l, i) => (
                      <div key={i} data-testid={`gate-line-${i}`} className="grid grid-cols-12 gap-2">
                        <Input className="col-span-6" data-testid={`gate-line-item-${i}`} value={l.item} onChange={(e) => setLine(i, "item", e.target.value)} placeholder="Item / goods" />
                        <Input className="col-span-3" type="number" data-testid={`gate-line-qty-${i}`} value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} placeholder="Qty" />
                        <Input className="col-span-2" data-testid={`gate-line-unit-${i}`} value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} placeholder="unit" />
                        <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-muted-foreground hover:text-destructive" data-testid={`gate-line-del-${i}`} onClick={() => removeLine(i)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="space-y-1"><Label className="text-xs">Vehicle No</Label>
                    <Input data-testid="gate-vehicle" value={f.vehicle_no} onChange={(e) => set("vehicle_no", e.target.value)} placeholder="RJ19 AB 1234" className={uRing("vehicle_no")} /></div>
                  <div className="space-y-1"><Label className="text-xs">Driver / person</Label>
                    <Input data-testid="gate-person" value={f.person} onChange={(e) => set("person", e.target.value)} className={uRing("person")} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Driver phone</Label>
                    <Input data-testid="gate-driver-phone" value={f.driver_phone} onChange={(e) => set("driver_phone", e.target.value)} placeholder="PhonePe / GPay / Paytm no" className={uRing("driver_phone")} /></div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-2 rounded-md border border-border p-3">
                    <Label className="flex items-center gap-1 text-xs"><ImagePlus className="h-3.5 w-3.5 text-primary" /> Material photos</Label>
                    <div className="flex flex-wrap gap-2">
                      {(f.photos || []).map((p, i) => (
                        <div key={i} className="relative" data-testid={`gate-photo-${i}`}>
                          <img src={authFileUrl(p)} alt="" className="h-14 w-14 rounded border border-border object-cover" />
                          <button type="button" onClick={() => removeAt("photos", i)} className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-background p-0.5"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                      <label className="flex h-14 w-14 cursor-pointer items-center justify-center rounded border border-dashed border-primary/50 text-primary hover:bg-primary/5"><Camera className="h-4 w-4" />
                        <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="gate-photos-upload" onChange={(e) => e.target.files.length && uploadFiles([...e.target.files], "photos", true)} /></label>
                    </div>
                  </div>
                  <div className="space-y-2 rounded-md border border-border p-3">
                    <Label className="flex items-center gap-1 text-xs"><Paperclip className="h-3.5 w-3.5 text-primary" /> Additional documents</Label>
                    <div className="space-y-1">
                      {(f.documents || []).map((d, i) => (
                        <div key={i} data-testid={`gate-doc-file-${i}`} className="flex items-center justify-between rounded border border-border px-2 py-1 text-xs">
                          <span className="flex items-center gap-1 truncate"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.name}</span></span>
                          <button type="button" onClick={() => removeAt("documents", i)}><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                      <label className="flex w-fit cursor-pointer items-center gap-1 rounded-md border border-dashed border-primary/50 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5"><Upload className="h-3.5 w-3.5" /> Add files
                        <input type="file" multiple className="hidden" data-testid="gate-docs-upload" onChange={(e) => e.target.files.length && uploadFiles([...e.target.files], "documents", false)} /></label>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border border-border p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-heading text-sm font-bold">Freight</p>
                    <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="gate-own-vehicle"
                      onClick={() => setF((p) => ({ ...p, vehicle_no: "RJ 14 GK 6773", freight_mode: "own_vehicle" }))}>
                      <Truck className="h-3 w-3" /> Own Tata Ace (RJ 14 GK 6773)
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div className="space-y-1"><Label className="text-xs">Freight</Label>
                      <Select value={f.freight_mode} onValueChange={(v) => set("freight_mode", v)}>
                        <SelectTrigger data-testid="gate-freight-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own_vehicle">Own vehicle (no freight)</SelectItem>
                          <SelectItem value="in_bill">Included in bill</SelectItem>
                          <SelectItem value="to_pay">To pay (freight payable)</SelectItem>
                        </SelectContent>
                      </Select></div>
                    {f.freight_mode === "to_pay" && (<>
                      <div className="space-y-1"><Label className="text-xs">Freight amount ₹</Label>
                        <Input type="number" data-testid="gate-freight-amount" value={f.freight_amount} onChange={(e) => set("freight_amount", e.target.value)} className={uRing("freight_amount")} /></div>
                      <div className="space-y-1"><Label className="text-xs">Pay via</Label>
                        <Select value={f.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
                          <SelectTrigger data-testid="gate-pay-mode"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem><SelectItem value="phonepe">PhonePe</SelectItem>
                            <SelectItem value="gpay">Google Pay</SelectItem><SelectItem value="paytm">Paytm</SelectItem>
                            <SelectItem value="bank">Bank</SelectItem>
                          </SelectContent>
                        </Select></div>
                    </>)}
                  </div>
                </div>

                <div className="space-y-1"><Label className="text-xs">Notes</Label>
                  <Textarea rows={2} data-testid="gate-remarks" value={f.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Any note about this entry — condition, pending docs, instructions…" /></div>
              </div>
              <DialogFooter><Button data-testid="gate-save" onClick={save} disabled={!f.company_id || !f.factory_id || !f.lines.some((l) => l.item)}>Save Entry</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="rounded-md border-border p-4 shadow-none">
          <div className="flex items-center gap-2 text-muted-foreground"><ArrowDownToLine className="h-4 w-4 text-sage" /><p className="text-xs font-medium uppercase">Inward entries</p></div>
          <p className="mt-1 font-heading text-2xl font-bold" data-testid="gate-in-count">{todayIn}</p>
        </Card>
        <Card className="rounded-md border-border p-4 shadow-none">
          <div className="flex items-center gap-2 text-muted-foreground"><ArrowUpFromLine className="h-4 w-4 text-ochre" /><p className="text-xs font-medium uppercase">Outward entries</p></div>
          <p className="mt-1 font-heading text-2xl font-bold" data-testid="gate-out-count">{todayOut}</p>
        </Card>
        <Card className="rounded-md border-border p-4 shadow-none lg:col-span-2">
          <div className="flex items-center gap-2 text-muted-foreground"><Truck className="h-4 w-4 text-primary" /><p className="text-xs font-medium uppercase">Job-work reconciliation</p></div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
            <span>Out: <b>{jw.out}</b></span><span>Returned: <b>{jw.inn}</b></span>
            <span className={jw.pending > 0 ? "text-ochre" : "text-sage"} data-testid="gate-jobwork-pending">Pending return: <b>{jw.pending}</b></span>
          </div>
        </Card>
        <Card className="rounded-md border-border p-4 shadow-none lg:col-span-2">
          <div className="flex items-center gap-2 text-muted-foreground"><IndianRupee className="h-4 w-4 text-ochre" /><p className="text-xs font-medium uppercase">Freight (to-pay)</p></div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
            <span className="text-destructive" data-testid="gate-freight-pending">Pending: <b>{money(freight.pending)}</b></span>
            <span className="text-sage">Paid: <b>{money(freight.paid)}</b></span>
            <span className="text-muted-foreground">Total: <b>{money(freight.total)}</b></span>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {companies.length > 1 && (
          <div className="w-56"><Select value={tCompany} onValueChange={setTCompany}>
            <SelectTrigger data-testid="gate-filter-company" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        )}
        <div className="w-44"><Select value={tFactory} onValueChange={setTFactory}>
          <SelectTrigger data-testid="gate-filter-factory" className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All factories</SelectItem>{factories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select></div>
        {[["all", "All types"], ...TYPES.map((t) => [t.v, t.l])].map(([v, l]) => (
          <button key={v} data-testid={`gate-filter-${v}`} onClick={() => setTType(v)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${tType === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={DoorOpen} title="No gate entries yet" hint="Record goods moving in or out with their invoice or challan." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Date</TableHead><TableHead>Dir</TableHead><TableHead>Company</TableHead><TableHead>Type</TableHead>
                  <TableHead>Factory</TableHead><TableHead>Party</TableHead><TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead><TableHead>Doc</TableHead><TableHead>Vehicle</TableHead>
                  <TableHead>Freight</TableHead><TableHead>Guard</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((e) => (
                  <TableRow key={e.id} data-testid={`gate-row-${e.id}`} className="hover:bg-muted/40">
                    <TableCell className="whitespace-nowrap text-xs">{(e.entry_date || e.created_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-[11px] ${e.direction === "in" ? "border-sage/40 bg-sage/10 text-sage" : "border-ochre/40 bg-ochre/10 text-ochre"}`}>{e.direction === "in" ? "IN" : "OUT"}</Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full border-primary/30 bg-primary/5 text-[10px]" data-testid={`gate-company-${e.id}`}>{(e.company_name || "").replace(/ Pvt Ltd$/, "").replace(/ Distributors$/, "") || "—"}</Badge></TableCell>
                    <TableCell className="text-xs">{TYPE_L[e.gate_type] || e.gate_type}</TableCell>
                    <TableCell className="text-xs">{e.factory_name || "—"}{e.gate_type === "transfer" && e.to_factory_name ? ` → ${e.to_factory_name}` : ""}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.party || "—"}</TableCell>
                    <TableCell className="text-xs">{e.item || "—"}{(e.lines && e.lines.length > 1) ? <span className="ml-1 text-[10px] font-semibold text-primary" data-testid={`gate-lines-more-${e.id}`}>+{e.lines.length - 1} more</span> : ""}</TableCell>
                    <TableCell className="whitespace-nowrap text-right text-xs">{e.quantity ? `${e.quantity} ${e.unit}` : "—"}</TableCell>
                    <TableCell className="text-xs">
                      {e.attachment
                        ? <a href={authFileUrl(e.attachment)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline" data-testid={`gate-doc-link-${e.id}`}><FileText className="h-3.5 w-3.5" />{(e.doc_type || "doc")}{e.doc_no ? ` ${e.doc_no}` : ""}</a>
                        : <span className="text-muted-foreground">{e.doc_no ? `${e.doc_type} ${e.doc_no}` : "—"}</span>}
                      {((e.photos || []).length + (e.documents || []).length) > 0 && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground" data-testid={`gate-files-${e.id}`}><Paperclip className="h-3 w-3" />{(e.photos || []).length + (e.documents || []).length}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.vehicle_no || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {e.freight_mode === "to_pay" ? (
                        <span className="flex items-center gap-1.5">
                          <span>{money(e.freight_amount)}</span>
                          <Badge variant="outline" className={`rounded-full text-[10px] ${e.freight_status === "paid" ? "border-sage/40 bg-sage/10 text-sage" : "border-destructive/30 bg-destructive/10 text-destructive"}`}>{e.freight_status === "paid" ? (e.payment_mode || "paid") : "pending"}</Badge>
                          {canPayFreight && e.freight_status !== "paid" && <Button variant="ghost" size="sm" className="h-6 px-1 text-[11px] text-primary" data-testid={`gate-pay-${e.id}`} onClick={() => payFreight(e)}>Mark paid</Button>}
                        </span>
                      ) : <span className="text-muted-foreground">{e.freight_mode === "in_bill" ? "In bill" : "Own veh."}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.created_by || "—"}</TableCell>
                    <TableCell className="text-right">
                      {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`gate-del-${e.id}`} onClick={() => remove(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
